import { DateTime } from 'luxon'
import { TGoalContribution, TGoalWeekActivity } from './goalTypes'

export type TWeekOutcome = TGoalWeekActivity | 'future'

export interface TWeekRow {
    /** "W3": weeks count from the goal's first week. */
    label: string
    /** "Oct 12–18" */
    range: string
    /** yyyy-MM-dd of the week's Monday: the server buckets goal weeks Monday to Sunday in local time. */
    from: string
    outcome: TWeekOutcome
    isCurrent: boolean
}

/**
 * One row per week the server counted, oldest first. The last of `weeks` is always the week holding
 * `asOf`, the local day the server counted them for, so rows are placed from that day and never
 * from the start date (which may fall mid-week) or this tab's clock (which may have rolled past it).
 */
export const buildWeekRows = (weeks: TGoalWeekActivity[], asOf?: string): TWeekRow[] => {
    const currentWeek = (asOf ? DateTime.fromISO(asOf) : DateTime.now()).startOf('week')
    const last = weeks.length - 1
    return weeks.map((activity, i) => {
        const from = currentWeek.minus({ weeks: last - i })
        const to = from.plus({ days: 6 })
        const range =
            from.month === to.month
                ? `${from.toFormat('MMM d')}–${to.toFormat('d')}`
                : `${from.toFormat('MMM d')}–${to.toFormat('MMM d')}`
        return {
            label: `W${i + 1}`,
            range,
            from: from.toISODate(),
            // The current week is still open: nothing logged yet isn't a miss.
            outcome: i === last && activity === 'none' ? 'future' : activity,
            isCurrent: i === last,
        }
    })
}

/** Newest first: the rows a person reads, most relevant on top. */
export const visibleWeekRows = (rows: TWeekRow[]): TWeekRow[] => [...rows].reverse()

/** How long until just after the coming local midnight: when the goals list is read again. */
export const msUntilNextLocalDay = (now = DateTime.now()) =>
    now.plus({ days: 1 }).startOf('day').diff(now).as('milliseconds') + 1000

export type TDayState = 'done' | 'today' | 'none' | 'future'

export interface TDayCell {
    /** Narrow weekday: "M", "T", … */
    letter: string
    date: string
    state: TDayState
}

/**
 * The current week's seven days, each done when a contribution with a time landed on it. Only this
 * week carries per-unit times (`thisWeekUnits`), so past weeks are drawn as a single bar instead.
 */
export const currentWeekDays = (from: string, units: TGoalContribution[], now = DateTime.now()): TDayCell[] => {
    const start = DateTime.fromISO(from).startOf('day')
    const today = now.startOf('day')
    const doneDays = new Set(
        units
            .map((unit) => (unit.at ? DateTime.fromISO(unit.at) : null))
            .filter((at): at is DateTime => !!at?.isValid)
            .map((at) => at.toLocal().toISODate())
    )
    return Array.from({ length: 7 }, (_, i) => {
        const day = start.plus({ days: i })
        const date = day.toISODate()
        const state: TDayState = doneDays.has(date)
            ? 'done'
            : +day === +today
            ? 'today'
            : day > today
            ? 'future'
            : 'none'
        return { letter: day.toFormat('ccccc'), date, state }
    })
}

/** "W3 • 1 out of 3" this week; "W2 • Complete" / "Partial" / "Missed" before it. */
export const weekSummary = (row: TWeekRow, done: number, target: number, unit?: 'hrs'): string => {
    if (row.isCurrent) {
        const amount = (n: number) => String(Math.round(n * 10) / 10)
        return `${row.label} • ${amount(done)} out of ${amount(target)}${unit === 'hrs' ? ' hrs' : ''}`
    }
    const word = row.outcome === 'complete' ? 'Complete' : row.outcome === 'partial' ? 'Partial' : 'Missed'
    return `${row.label} • ${word}`
}
